import {Group} from '@mantine/core';
import React from 'react';

import ReviewBadge from 'notebook/reviews/ReviewBadge';
import {useReviewController} from 'notebook/reviews/useReviewController';
import TemplatePublishingButton, {
  TemplatePublishingButtonProps,
} from 'template-collectibles/publishing-ui/TemplatePublishingButton';
import {PUBLISH_REQUIRES_APPROVAL_TOOLTIP} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import {CenteredSpinner} from 'ui-library/Spinner';

export interface TemplateNoteTitleBadgesProps {
  templateId: string;
  openVersionApiId: string;
  isLatest: boolean;
  isViewingPublishedVersion: boolean;
  publishingButtonProps: TemplatePublishingButtonProps;
}

export default function TemplateNoteTitleBadges({
  templateId,
  openVersionApiId,
  isLatest,
  isViewingPublishedVersion,
  publishingButtonProps,
}: TemplateNoteTitleBadgesProps): JSX.Element {
  const isCurrentEditableDraft = isLatest && !isViewingPublishedVersion;
  const controller = useReviewController(templateId, {
    enableReviewLifecycle: true,
    openVersionId: openVersionApiId,
    isCurrentEditableDraft,
    cyclicalPublishGating: {
      isLatest,
      isPublishOptionDisabledFromParent: publishingButtonProps.isPublishOptionDisabled,
    },
  });

  const publishState = controller.publishState;
  const showReviewBadge =
    publishState != null &&
    !publishState.loading &&
    !publishState.error &&
    !publishState.inaccessible &&
    publishState.reviewRequired;

  return (
    <Group spacing={8} align="center" display="inline-flex">
      {publishState?.loading && <CenteredSpinner />}
      {showReviewBadge && (
        <ReviewBadge
          reviewableId={templateId}
          controller={controller}
          enableReviewLifecycle={true}
          openVersionId={openVersionApiId}
          isCurrentEditableDraft={isCurrentEditableDraft}
        />
      )}
      <TemplatePublishingButton
        {...publishingButtonProps}
        isPublishOptionDisabled={
          publishState?.isPublishOptionDisabled ?? publishingButtonProps.isPublishOptionDisabled
        }
        publishDisabledTooltip={
          publishState?.isPublishBlockedByReview ? PUBLISH_REQUIRES_APPROVAL_TOOLTIP : undefined
        }
      />
    </Group>
  );
}
